import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext.tsx";
import type { Connector } from "../data/connectors";

const API_BASE = process.env.NODE_ENV==="production" ? "/api" : (process.env.REACT_APP_BACKEND_URL || "/api");
export type SourceStatus = "connected" | "unverified" | "available" | "coming-soon";

export function useSourceConnections() {
  const { user, isAuthenticated, isLoading, loginWithGoogle, loginWithWhoop, loginWithWithings } = useAuth();
  const [usernames, setUsernames] = useState<Record<string,string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isAuthenticated) {
      setUsernames({});
      setError("");
      return;
    }
    let alive = true;
    setLoading(true);
    fetch(API_BASE+"/connections",{ credentials:"include" })
      .then(async response => {
        if (!response.ok) throw new Error(response.status === 500
          ? "Username saving needs the database migration to be applied."
          : "Could not load your saved usernames.");
        return response.json();
      })
      .then(data => {
        if (!alive) return;
        const items: Array<{provider:string;username:string}> = Array.isArray(data.connections) ? data.connections : [];
        setUsernames(Object.fromEntries(items.map(c=>[c.provider,c.username])));
        setError("");
      })
      .catch(e=> { if(alive) setError(e instanceof Error?e.message:"Could not load connections"); })
      .finally(()=>{ if(alive) setLoading(false); });
    return ()=>{alive=false};
  },[isAuthenticated]);

  const getStatus = (source:Connector):SourceStatus => {
    if (source.key==="google") return isAuthenticated ? "connected" : "available";
    if (source.key==="whoop") return user?.whoopId ? "connected" : "available";
    if (source.key==="withings") return user?.withingsUserId ? "connected" : "available";
    if (source.method==="evidence") return "available";
    if (source.method==="username") return usernames[source.key] ? "unverified" : "available";
    return "coming-soon";
  };
  const connect = (source:Connector) => {
    if (source.key==="google") return loginWithGoogle();
    if (!isAuthenticated) return loginWithGoogle();
    if (source.key==="whoop") return loginWithWhoop();
    if (source.key==="withings") return loginWithWithings();
  };
  const saveUsername = async (provider:string, username:string|null) => {
    setLoading(true);
    setError("");
    try {
      const response=await fetch(API_BASE+"/connections",{
        method:username===null?"DELETE":"PUT",
        credentials:"include",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({provider,username}),
      });
      if(!response.ok) {
        const result=await response.json().catch(()=>null);
        throw new Error(result?.error || "Unable to save username. Check your connection and database migration.");
      }
      setUsernames(old=>{
        const updated={...old};
        if(username===null) delete updated[provider];
        else updated[provider]=username.trim();
        return updated;
      });
      return true;
    } catch(e) {
      setError(e instanceof Error ? e.message : "Unable to save username");
      return false;
    } finally {
      setLoading(false);
    }
  };
  const counts=useMemo(()=>({
    claimed:Object.keys(usernames).length,
    verified:(isAuthenticated?1:0)+(user?.whoopId?1:0)+(user?.withingsUserId?1:0),
  }),[usernames,isAuthenticated,user?.whoopId,user?.withingsUserId]);

  return { user, isAuthenticated, isLoading, loading, error, usernames, counts, getStatus, connect, saveUsername, clearError:()=>setError("") };
}
